import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useRef } from "react";
import {
  SubmitHandler,
  useForm,
  useFormState,
  useWatch
} from "react-hook-form";

import KeyIcon from "@mui/icons-material/Key";
import LoadingButton from "@mui/lab/LoadingButton";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { primary } from "app/themes/colors";
import EmailField from "components/email-field";
import PasswordField from "components/password-field";
import { ENABLE_SSO_INVITATIONS } from "constants/feature-flags";
import {
  loginUser,
  selectAuthErrorMessage,
  selectAuthIsFetching
} from "features/auth/auth-slice";
import { startSso } from "utils/sso.util";

interface IFormInput {
  email: string;
  password: string;
  keepLoggedIn: boolean;
}

export default function Login() {
  const loginButtonRef = useRef<HTMLButtonElement>(null);
  const dispatch = useAppDispatch();
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm<IFormInput>({
    mode: "onChange",
    defaultValues: { email: "", password: "", keepLoggedIn: true }
  });
  const { isValid } = useFormState({
    control
  });
  const isFetching = useAppSelector(selectAuthIsFetching);
  const errorMessage = useAppSelector(selectAuthErrorMessage);
  const flags = useFlags();
  const isSsoEnabled = flags[ENABLE_SSO_INVITATIONS] ?? false;
  const onSubmit: SubmitHandler<IFormInput> = (loginData) => {
    dispatch(loginUser(loginData));
  };
  const handleOnBlur = useCallback(() => {
    if (loginButtonRef.current) loginButtonRef.current.focus();
  }, []);
  const keepLoggedIn = useWatch({ control, name: "keepLoggedIn" });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={1}>
        <Stack gap={1.5}>
          <EmailField
            name="email"
            label="Email Address"
            control={control}
            errors={errors}
            required
          />

          <PasswordField
            name="password"
            label="Password"
            control={control}
            onBlur={handleOnBlur}
          />
          <Link
            href="/forgot-password"
            color={primary[500]}
            sx={{
              cursor: "pointer",
              display: "block",
              fontWeight: "bold"
            }}
            underline="hover"
          >
            Forgot your password?
          </Link>
        </Stack>
        <LoadingButton
          ref={loginButtonRef}
          type="submit"
          variant="contained"
          loading={isFetching}
          disabled={!isValid}
          sx={{ mt: 3 }}
        >
          Sign In
        </LoadingButton>

        {Boolean(errorMessage) && (
          <Alert icon={false} severity="error" sx={{ marginTop: 4 }}>
            {errorMessage}
          </Alert>
        )}

        <FormControlLabel
          control={
            <Checkbox
              checked={keepLoggedIn}
              onChange={(event) =>
                setValue("keepLoggedIn", event.target.checked, {
                  shouldDirty: true,
                  shouldValidate: true
                })
              }
            />
          }
          sx={{ "& span": { fontWeight: 600 } }}
          label="Keep me logged in"
        />

        {isSsoEnabled && (
          <>
            <Divider sx={{ mt: 2 }}>or</Divider>

            {/* Single configured WorkOS connection — one entry, no provider
                choice. Full-page redirect; the backend owns the OAuth dance. */}
            <Button
              type="button"
              variant="outlined"
              fullWidth
              startIcon={<KeyIcon />}
              onClick={() => startSso()}
              data-testid="sso-sign-in"
            >
              Sign in with SSO
            </Button>
          </>
        )}
      </Stack>
    </form>
  );
}
